import { css } from '@emotion/core'
import { graphql } from 'gatsby'
import { renderRichText } from 'gatsby-source-contentful/rich-text'
import React from 'react'

import Anchors from '../../components/Anchors'
import Block from '../../components/Block'
import Body from '../../components/Body'
import CTAEbook from '../../components/ctas/CTAEbook'
import CTATemplates from '../../components/ctas/CTATemplates'
import Divider from '../../components/Divider'
import Footer from '../../components/Footer'
import Header from '../../components/Header'
import { IntroHeroBlock } from '../../components/IntroHeroBlock'
import Layout from '../../components/Layout'
import ReadingProgressBar from '../../components/ReadingProgressBar'
import RichText from '../../components/RichText'
import ScrollToTop from '../../components/ScrollToTop'
import SEO from '../../components/SEO'
import SocialSharing from '../../components/SocialSharing'
import { breakpoints } from '../../constants/breakPoints'
import { longCopyBreakPoint, longCopyMobileBreakPoint } from '../../constants/breakPoints'
import { richTextRenderingOptions } from '../../helpers/contentful'
import { getCtaEbookUrl, getCtaTemplatesCategoryUrl } from '../../helpers/cta'
import { ChapterProps } from '../../interfaces/guides'

const IndexPage: React.FunctionComponent<ChapterProps> = ({
  data: {
    guideOverview: {
      slug,
      metaImage,
      cardImage,
      title,
      description: { description },
    },
    eBookChapters: { edges },
  },
}) => {
  const chapters = React.useMemo(() => edges.map(({ node }) => node), [edges])
  const currentId =
    typeof window !== `undefined` && window.location.hash
      ? `${window.location.pathname}${window.location.hash}`.slice(0, -1)
      : `${`/resources/guides/${slug}/`}#${chapters[0].number}`

  const anchors = React.useMemo(
    () =>
      chapters.map(({ heading, number, content }, index) => ({
        id: `${`/resources/guides/${slug}/`}#${number}`,
        label: `Chapter ${number}: ${heading}`,
        mobileLabel: `Chapter ${number}`,
        hash: number,
        subAnchors:
          content.references.length > 0
            ? content.references
                .filter(({ __typename }) => __typename === 'ContentfulChapterSubheading')
                .map(({ contentful_id, subheading }) => ({ id: contentful_id, label: subheading!.subheading }))
            : undefined,
        index,
      })),
    [chapters]
  )

  const metaImageSrc = !!metaImage ? `https:${metaImage.file.url}` : `https:${cardImage.file.url}`

  return (
    <Layout whiteBG>
      <SEO
        lang="en"
        title={title.replace('<br class="hide-on-small" />', '')}
        description={description.replace(/<br class="hide-on-small" \/>/g, '')}
        imageSrc={metaImageSrc}
      />
      <Header hideOnScroll />
      <Body>
        <ReadingProgressBar />

        <ScrollToTop />

        <IntroHeroBlock
          headlineText={title}
          subheaderText={description}
          noMaxWidthForSubtitle
          blueBG
          withoutCTA
          withoutMarginBottom
        />

        <Block css={longCopyBlockStyle}>
          <Anchors anchors={anchors} currentId={currentId} />
          <div css={richTextContainerStyle}>
            {chapters.map(({ content, number, heading }) => {
              return (
                <div key={`${`/resources/guides/${slug}/`}#${number}`} css={chapterContainerStyle}>
                  <h4 className="subtitle blue-text">CHAPTER {number}</h4>
                  <h2 className="heading2" id={`${`/resources/guides/${slug}/`}#${number}`} css={chapterHeadingStyle}>
                    {heading}
                  </h2>
                  <RichText className="ebook_rich_text">{renderRichText(content, richTextRenderingOptions)}</RichText>
                </div>
              )
            })}
            <Divider size={2} isTop={false} notDecreased />
            <SocialSharing title={title} />
          </div>
          <CTATemplates templatesCategoryUrl={getCtaTemplatesCategoryUrl(slug)} isSidebarCta />
        </Block>

        <CTAEbook isCtaForGuidePage educationResourceUrl={getCtaEbookUrl(slug)} />
      </Body>
      <Footer />
    </Layout>
  )
}

export default IndexPage

export const query = graphql`
  query ($id: String!) {
    eBookChapters: allContentfulEBookChapter(
      sort: { fields: [number, subNumber], order: ASC }
      filter: { inDepthGuide: { id: { eq: $id } } }
    ) {
      edges {
        node {
          heading
          number
          content {
            raw
            references {
              ... on ContentfulChapterSubheading {
                contentful_id
                __typename
                subheading {
                  subheading
                }
              }
              ... on ContentfulAsset {
                # contentful_id and __typename are required to resolve the references
                title
                contentful_id
                __typename
                gatsbyImageData(width: 800, quality: 90)
              }
              ... on ContentfulDefinitionCard {
                id
                __typename
                contentful_id
                title
                description {
                  description
                }
              }
              ... on ContentfulCrossSellingEntry {
                id
                type
                ebookResource {
                  slug
                }
                contentful_id
                __typename
                title
              }
              ... on Node {
                ... on ContentfulImageWithLink {
                  id
                  contentful_id
                  __typename
                  link
                  image {
                    title
                    gatsbyImageData(width: 800, quality: 90)
                  }
                  imageDescription: childContentfulImageWithLinkDescriptionTextNode {
                    description
                  }
                }
              }
            }
          }
        }
      }
    }

    guideOverview: contentfulEducationResource(id: { eq: $id }) {
      slug
      cardImage {
        file {
          url
        }
      }
      metaImage {
        file {
          url
        }
      }
      title
      description {
        description
      }
    }
  }
`

const chapterHeadingStyle = css`
  padding-top: 125px !important;
  margin-top: -125px !important;
  margin-bottom: 30px !important;

  @media screen and (max-width: ${breakpoints.medium}px) {
    padding-top: 100px !important;
    margin-top: -100px !important;
  }
`

const richTextContainerStyle = css`
  min-width: 1rem;
`

const chapterContainerStyle = css`
  position: relative;
`

export const longCopyBlockStyle = css`
  grid-template-columns: 2.5fr 7.25fr 2.5fr;
  column-gap: 2rem;
  align-items: start;
  margin: 4rem 0 8rem;
  position: relative;

  .ebook_rich_text {
    padding-bottom: 3.75rem;

    // for subheadings we add positive padding-top and negative margin-top (see contentful.tsx)
    > :first-child:not(.subheading) {
      margin-top: 0 !important;
    }

    @media screen and (max-width: ${breakpoints.medium}px) {
      padding-bottom: 0; // next / previous buttons are sticky at top now
    }
  }

  @media screen and (max-width: ${longCopyBreakPoint}px) {
    grid-template-columns: calc(10rem + 8%) auto;
    column-gap: 3%;
  }

  @media screen and (max-width: ${longCopyMobileBreakPoint}px) {
    grid-template-columns: 1fr;
    margin-bottom: 6rem;
  }
`

// const ctaStyle = css`
//   min-height: auto;
//   overflow: visible;

//   .body-1 {
//     max-width: 39rem;
//   }

//   @media screen and (max-width: ${breakpoints.medium}px) {
//     margin-top: 12.5rem;
//   }
// `

// const imgStyle = css`
//   position: absolute !important;
//   bottom: 0;
//   right: 5rem;
//   width: 16rem;
//   height: max-content;
//   transition: all 0.2s ease-in-out;

//   &:hover {
//     transform: scale(1.03);
//   }

//   @media screen and (max-width: ${breakpoints.large}px) {
//     width: 27%;
//     right: 3rem;
//   }

//   @media screen and (max-width: ${breakpoints.medium}px) {
//     width: 10rem;
//     top: -10rem;
//     left: 0;
//   }

//   @media screen and (max-width: ${breakpoints.small}px) {
//     left: -0.5rem;
//   }
// `
